/**
 * Grade-band gating for the family day view (`/orders/family/day`).
 *
 * A campus can run the SAME item at two portion sizes as two menu instances
 * tagged for different bands (e.g. K-5 and 6-8). The day view must show each
 * child ONLY their band's instance — portion derives from the student, never
 * a parent choice — so the per-child item list filters whole menus with the
 * shared gate (`menuAllowsGradeRangeCode`, the one matching rule platform
 * wide) before flattening by mealtime. No dedupe anywhere: two sizes stay
 * two menus, each visible only to its own audience.
 *
 * Subjects that are band-less BY DESIGN — staff self-diners, who hold
 * TEACHER (not STUDENT) memberships — skip the gate (`bandGated: false`) and
 * see every menu. A STUDENT with no explicit grade→band match is fail-closed
 * (2026-07 flip): zero menus, the day view's empty state renders. The server
 * enforces the same rule authoritatively at submit
 * (422 `menu_not_allowed_for_grade`).
 */

import { menuAllowsGradeRangeCode } from '@ordo/constants/menu-grade-range-gate'

/** Per-(child, campus) gate context, derived from `OrderingStudent.locations`. */
export interface ChildBandContext {
  /** The child's central `User.id` — matched against a menu's entitled list. */
  userId: string
  /** False = band-less by design (staff self-diner) — sees every menu. */
  bandGated: boolean
  /** Explicit school grade→band match; null = unmatched (blocked when gated). */
  gradeRangeCode: string | null
}

interface GateableMenu {
  id?: string
  /**
   * Audience tier stamped by `GET /api/menus`. Absent on older payloads and
   * on ordo-team responses, which are treated as CUSTOMER.
   */
  /** ELECTED = a field trip meal the school opened to this household for the date; gated per child like ENTITLED. */
  audienceTier?: 'CUSTOMER' | 'ENTITLED' | 'ELECTED'
  /**
   * On the ENTITLED tier only: the students in THIS household entitled to the
   * menu. The server already dropped menus no one here may see, so this list
   * is never empty when present — it exists to split WITHIN a household.
   */
  entitledStudentUserIds?: string[]
  /**
   * Students in THIS household the menu is closed to on the date (a
   * field-trip-only election: they may order only the field trip meal).
   */
  blockedStudentUserIds?: string[]
  name?: string
  /** Customer-facing section title ("Mains") — wins over `name` when set. */
  displayName?: string | null
  /** Org-wide display position — menus render as sections in this order. */
  sortOrder?: number
  isArchived: boolean
  /** The menu spans the date but lists it as a no-service exception. */
  blockedOnDate?: boolean
  gradeRangeCodes?: string[] | null
}

interface FlattenableItem {
  visible: boolean
  isArchived: boolean
  sortOrder: number
}

/**
 * The owning-menu section stamped onto each flattened item so the day view
 * can render menus as sections ("Mains", "Sides, Drinks & Desserts") the way
 * the school order sheet does. Optional: rows appended outside the flatten
 * (special-diet additions) carry none and ride the section they follow.
 */
export interface MenuGroupAnnotation {
  menuGroupId?: string
  /** `displayName ?? name` of the owning menu. */
  menuGroupTitle?: string
  menuGroupSort?: number
  /** The owning menu's audience tier — ELECTED items render as the field trip meal. */
  menuAudienceTier?: 'CUSTOMER' | 'ENTITLED' | 'ELECTED'
}

/**
 * Shared-gate call for one menu (the only sanctioned matching rule).
 *
 * Two independent gates, both of which must pass:
 *
 *  1. ENTITLEMENT — a restricted-diet menu is visible only to the students
 *     entitled to it. `GET /api/menus` serves ONE payload per household, so
 *     the server can only filter to "someone here may see this"; splitting it
 *     per child has to happen here. Without this gate a household with two
 *     children would show child A's medical menu to child B. Checked FIRST
 *     and independently of `bandGated`: a staff self-diner skips the band
 *     gate but must never skip this one.
 *
 *  2. GRADE BAND — unchanged.
 *
 * The server re-enforces both at submit; this is presentation, not security.
 */
export function menuAllowedForChild(menu: GateableMenu, child: ChildBandContext): boolean {
  if (
    (menu.audienceTier === 'ENTITLED' || menu.audienceTier === 'ELECTED') &&
    !(menu.entitledStudentUserIds ?? []).includes(child.userId)
  ) {
    return false
  }
  // Field-trip-only day: this child may order nothing but the field trip meal.
  if ((menu.blockedStudentUserIds ?? []).includes(child.userId)) {
    return false
  }
  if (!child.bandGated) {
    return true
  }
  return menuAllowsGradeRangeCode(menu.gradeRangeCodes ?? [], child.gradeRangeCode)
}

/**
 * Flatten a day's menus into per-mealtime item lists for one child: skip
 * archived / blocked-on-date menus, apply the band gate, keep visible
 * non-archived items. Ordering is MENU-major (menu `sortOrder`, then item
 * `sortOrder` within a menu) so each menu's items stay contiguous — menus
 * render as sections on the day view, titled `displayName ?? name` via the
 * `MenuGroupAnnotation` stamped on every row. This is the day view's single
 * source for ORDERABLE rows (add + edit steppers); receipts render from the
 * ungated flatten so a placed order stays visible even when its menu is no
 * longer offered to the child.
 */
export function orderableItemsByMealtime<TMealtime extends string, TItem extends FlattenableItem>(
  menus: ReadonlyArray<GateableMenu & { mealtime: TMealtime; items: readonly TItem[] }>,
  child: ChildBandContext,
): Map<TMealtime, Array<TItem & MenuGroupAnnotation>> {
  const map = new Map<TMealtime, Array<TItem & MenuGroupAnnotation>>()
  for (const menu of menus) {
    if (menu.isArchived || menu.blockedOnDate || !menuAllowedForChild(menu, child)) {
      continue
    }
    const visible = menu.items.filter((it) => it.visible && !it.isArchived)
    if (visible.length === 0) {
      continue
    }
    const title = menu.displayName ?? menu.name
    const list = map.get(menu.mealtime) ?? []
    list.push(
      ...visible.map((it) => ({
        ...it,
        ...(menu.id !== undefined ? { menuGroupId: menu.id } : {}),
        ...(title !== undefined ? { menuGroupTitle: title } : {}),
        ...(menu.audienceTier !== undefined ? { menuAudienceTier: menu.audienceTier } : {}),
        // Cached pre-`sortOrder` payloads may lack the field — default to 0.
        menuGroupSort: menu.sortOrder ?? 0,
      })),
    )
    map.set(menu.mealtime, list)
  }
  for (const [, list] of map) {
    list.sort(
      (a, b) =>
        (a.menuGroupSort ?? 0) - (b.menuGroupSort ?? 0) ||
        (a.menuGroupTitle ?? '').localeCompare(b.menuGroupTitle ?? '') ||
        a.sortOrder - b.sortOrder,
    )
  }
  return map
}

/**
 * FACTORY-643 — an ADULT diner's rows (not a student at the campus,
 * `isStudent: false`, e.g. a staff self-diner): each item's `customerPriceCents`
 * becomes its `adultPriceCents` when the school set one. Applied to the
 * diner's orderable rows only, so every price the day view shows, carts and
 * sends as the expected price is the one checkout charges (the server's
 * `adultDinerUserIds` uses the same `isStudent` rule).
 */
export function withAdultPrices<
  TMealtime extends string,
  TItem extends { customerPriceCents: number | null; adultPriceCents?: number | null },
>(itemsByMealtime: ReadonlyMap<TMealtime, readonly TItem[]>): Map<TMealtime, TItem[]> {
  const out = new Map<TMealtime, TItem[]>()
  for (const [mealtime, items] of itemsByMealtime) {
    out.set(
      mealtime,
      items.map((item) =>
        item.adultPriceCents != null ? { ...item, customerPriceCents: item.adultPriceCents } : item,
      ),
    )
  }
  return out
}

/**
 * The ungated flatten — receipts (placed orders must stay visible).
 *
 * `userId: ''` matches no entitled list, so an ENTITLED menu the child is not
 * entitled to stays filtered even here. A placed restricted line still renders
 * on the receipt: those rows come from the special-diet sidecar merge in
 * `family-day.tsx`, not from this flatten.
 */
export function allItemsByMealtime<TMealtime extends string, TItem extends FlattenableItem>(
  menus: ReadonlyArray<GateableMenu & { mealtime: TMealtime; items: readonly TItem[] }>,
): Map<TMealtime, Array<TItem & MenuGroupAnnotation>> {
  return orderableItemsByMealtime(menus, { userId: '', bandGated: false, gradeRangeCode: null })
}

// ---------------------------------------------------------------------------
// 422 `menu_not_allowed_for_grade` wire parsing (checkout line highlighting)
// ---------------------------------------------------------------------------

/** One offending line off the 422 `menu_not_allowed_for_grade` body. */
export interface BandBlockedLineWire {
  menuItemId: string
  studentUserId: string
}

/** Key a cart line for the band-blocked highlight set. */
export function bandBlockedLineKey(studentUserId: string, menuItemId: string): string {
  return `${studentUserId}::${menuItemId}`
}

/**
 * Parse the `lines` array off an unknown 422 `menu_not_allowed_for_grade`
 * body into highlight keys. Blocking is per (student, item) — the same item
 * can be valid for a sibling in the menu's band — so the server sends line
 * pairs; `menuItemIds` alone would over-highlight.
 */
export function parseBandBlockedLineKeys(body: unknown): string[] {
  if (!body || typeof body !== 'object') {
    return []
  }
  const raw = (body as { lines?: unknown }).lines
  if (!Array.isArray(raw)) {
    return []
  }
  const keys: string[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') {
      continue
    }
    const record = entry as Record<string, unknown>
    if (typeof record['menuItemId'] === 'string' && typeof record['studentUserId'] === 'string') {
      keys.push(bandBlockedLineKey(record['studentUserId'], record['menuItemId']))
    }
  }
  return keys
}
